// src/components/common/ErrorFallback.tsx
import React from 'react';
import { FallbackProps } from 'react-error-boundary';

const ErrorFallback: React.FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    const isDevelopment = process.env.NODE_ENV === 'development';

    const handleGoHome = () => {
        window.location.href = '/admin/dashboard';
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
            <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                {/* Icône d'erreur */}
                <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                </div>

                {/* Titre */}
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                    Oups ! Une erreur s'est produite
                </h1>

                {/* Message d'erreur */}
                <p className="text-gray-600 mb-6">
                    Nous sommes désolés, mais quelque chose s'est mal passé.
                    Notre équipe a été notifiée de ce problème.
                </p>

                {/* Détails de l'erreur en développement */}
                {isDevelopment && error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-left">
                        <h3 className="text-sm font-medium text-red-800 mb-2">
                            Détails de l'erreur (mode développement)
                        </h3>
                        <pre className="text-xs text-red-700 overflow-auto">
              {error.message}
            </pre>
                        {error.stack && (
                            <details className="mt-2">
                                <summary className="text-xs text-red-600 cursor-pointer">
                                    Stack trace
                                </summary>
                                <pre className="text-xs text-red-600 mt-1 overflow-auto">
                  {error.stack}
                </pre>
                            </details>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                        onClick={resetErrorBoundary}
                        className="flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Réessayer
                    </button>

                    <button
                        onClick={handleGoHome}
                        className="flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                        Accueil
                    </button>
                </div>

                {/* Informations de contact */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-xs text-gray-500">
                        Si le problème persiste, contactez notre support technique à{' '}
                        <a
                            href="mailto:support@lesbatisseurs.ga"
                            className="text-blue-600 hover:underline"
                        >
                            support@lesbatisseurs.ga
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ErrorFallback;
